import { useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { SERVICES } from "../lib/brand";
import { CTA, GlassCard, PageHeader, Shell } from "../components/site/Primitives";

const DETAIL = {
  problem: [
    "Enquiries arrive outside working hours and go unanswered.",
    "The first responder usually wins the job — and it isn't always you.",
    "Manual admin consumes hours that should go to delivery.",
  ],
  solution: [
    "A scoped AI agent handles the first interaction and captures structured data.",
    "Every interaction writes back to one CRM record with a clear next action.",
    "Escalation rules hand the conversation to a human at the right moment.",
  ],
  features: [
    "Defined scope and approved answers",
    "Structured data capture",
    "CRM write-back",
    "Escalation and handover rules",
    "Execution logging",
    "Reporting on outcomes",
  ],
  workflow: ["Trigger", "Capture", "Qualify", "Act", "Confirm", "Log", "Report"],
  benefits: [
    "Faster first response",
    "Consistent follow-up",
    "Fewer lost enquiries",
    "Less repetitive admin",
    "Clear operational visibility",
  ],
};

export default function Services() {
  const [openKey, setOpenKey] = useState(SERVICES[0][0]);

  return (
    <div data-testid="services-page">
      <PageHeader
        testId="services-title"
        eyebrow="Services"
        title="Automation built around how your business actually earns."
        sub="Each service is delivered as part of one operating system, with a defined scope, a documented workflow and measurable outcomes."
      />
      <section className="relative py-20">
        <Shell>
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
            <div className="grid gap-2">
              {SERVICES.map(([key, name, blurb], i) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`service-tab-${key}`}
                  onClick={() => setOpenKey(key)}
                  className={`glass glass-hover rounded-lg p-4 text-left ${
                    openKey === key ? "border-elv-green/35 bg-elv-green/[0.06]" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-elv-text">{name}</p>
                      <p className="mt-1.5 text-xs text-elv-dim">{blurb}</p>
                    </div>
                    <Icons.ChevronRight
                      className={`h-4 w-4 shrink-0 ${openKey === key ? "text-elv-green" : "text-elv-dim"}`}
                    />
                  </div>
                </button>
              ))}
            </div>

            <div className="lg:sticky lg:top-24 lg:self-start">
              <GlassCard testId="service-detail" hover={false} className="p-6 sm:p-8">
                <p className="text-[11px] uppercase tracking-[0.24em] text-elv-green">
                  {SERVICES.find(([k]) => k === openKey)[1]}
                </p>
                <h2 className="mt-4 text-2xl font-semibold text-elv-text sm:text-3xl">
                  {SERVICES.find(([k]) => k === openKey)[2]}
                </h2>

                {[
                  ["Problem", DETAIL.problem, Icons.AlertTriangle],
                  ["Solution", DETAIL.solution, Icons.Sparkles],
                ].map(([title, items, Icon]) => (
                  <div key={title} className="mt-7">
                    <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-elv-dim">
                      <Icon className="h-3.5 w-3.5 text-elv-cyan" />
                      {title}
                    </p>
                    <ul className="mt-3 space-y-2">
                      {items.map((it) => (
                        <li key={it} className="text-sm text-elv-dim">
                          — {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="mt-7">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">Features</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {DETAIL.features.map((f) => (
                      <span
                        key={f}
                        className="rounded-sm border border-white/[0.08] bg-white/[0.03] px-2.5 py-1 text-[11px] text-elv-dim"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-7">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">Workflow</p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {DETAIL.workflow.map((w, i, arr) => (
                      <span key={w} className="flex items-center gap-2">
                        <span className="rounded-sm border border-elv-green/20 bg-elv-green/[0.06] px-2.5 py-1 text-[11px] text-elv-text">
                          {w}
                        </span>
                        {i < arr.length - 1 ? (
                          <Icons.ArrowRight className="h-3 w-3 text-elv-green/70" />
                        ) : null}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-7">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">Benefits</p>
                  <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                    {DETAIL.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-elv-dim">
                        <Icons.Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-elv-green" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-9 flex flex-wrap gap-3">
                  <CTA to="/book-demo" testId="service-detail-cta">
                    Book a Free AI Demo
                  </CTA>
                  <Link
                    to="/pricing"
                    data-testid="service-detail-pricing"
                    className="px-2 py-3 text-sm text-elv-dim underline decoration-elv-green/40 underline-offset-4 hover:text-elv-text"
                  >
                    See pricing
                  </Link>
                </div>
              </GlassCard>
            </div>
          </div>
        </Shell>
      </section>
    </div>
  );
}
